/* eslint-disable react/jsx-pascal-case */
import React, { memo, useMemo } from 'react';
import { bool, shape, string } from 'prop-types';
import classNames from 'classnames/bind';
import { Col, Row, Placeholder } from '@thd-olt-component-react/core-ui';
import { AppContext, DynamicComponent } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  getContentfulTargetingIds,
  useVariationIds,
} from '@thd-olt-component-react/component-selector';
import { componentMapper } from '../dataModel/componentMapper';
import { HYDRATION_STANDARD_SCROLL_BUFFER, HYDRATION_EARLY_DELAY, HYDRATION_STANDARD_DELAY } from '../../constants';
import { parseContent, parseSponsoredContent } from '../utils/utilities';
import styles from './educational-layout.scss';

/**
 * Main job of this component is to just render the various harmony components only when necessary (see react pure
 * components).
 */
export const EducationalLayoutRenderer = memo(({
  canonicalURL, data, pageId, customerType, storeId, pageTypeComponents, thdSEOBotDetection
}) => {

  declareContexts([AppContext, CustomerContext]);
  const thdCustomer = useThdCustomer();
  const cx = classNames.bind(styles);
  const layouts = data?.layouts;
  const content = layouts?.content;
  const experienceHasSideNav = !!content?.sideNavigation?.id;

  const { mboxes, campaignIds } = getContentfulTargetingIds({ content });

  const isReady = mboxes.length > 0 || campaignIds.length > 0;

  const { variationIds, renderDefault } = useVariationIds({
    mboxes,
    campaignIds,
    isReady,
  });

  const customProps = {
    ComponentSelector: (component) => ({
      componentMap: componentMapper.getComponentMap(),
      defaultComponent: component.defaultComponent,
      variations: component.variations,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: thdSEOBotDetection,
      targetingType: component.targetingType,
      customPropMap: {
        Spotlight: {
          showDescription: true
        }
      }
    }),
    Spotlight: () => ({
      showDescription: true
    }),
    PromoVisualNavigation: () => ({
      experienceHasSideNav
    })
  };

  const { Content } = useMemo(() => parseContent({
    data,
    componentMapper,
    customProps
  }), [data, variationIds, renderDefault]);

  const { Sponsored } = useMemo(() => parseSponsoredContent({
    data,
    componentMapper,
    customProps
  }), [data]);

  const pageTitle = layouts?.title || '';
  const descriptionText = content?.descriptionNew?.descriptionText || '';
  const descriptionTitle = content?.descriptionNew?.title || '';
  const bottomCarouselCustomRenderId = content?.sponsoredBottomCarousel?.customRenderId;
  const middleBannerCustomRenderId = content?.sponsoredMiddleBanner?.customRenderId;
  const skyscraperBannerCustomRenderId = content?.sponsoredSkyscraperBanner?.customRenderId;
  const topBannerCustomRenderId = content?.sponsoredTopBanner?.customRenderId;
  const sponsoredBrowserId = thdCustomer?.mcvisID || '';

  const { landingPageBreadcrumbs, landingPageMetadata } = pageTypeComponents;

  /* Two heroCarousels are for backwards compat with the new heroCarousel contentType.
  Upper case works for the new and lower case works for old. */

  return (
    <Row name="educational-layout-manager">
      <ErrorBoundary name="metadata">
        {landingPageMetadata}
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('educational-layout__no-bottom-padding')}>
          <ErrorBoundary name="breadcrumbs">
            {landingPageBreadcrumbs}
          </ErrorBoundary>
        </Col>
        <Col xs="12" className={cx('educational-layout__no-top-bottom-padding')}>
          <ErrorBoundary name="sponsored-top-banner">
            <Sponsored.SponsoredTopBanner browserId={sponsoredBrowserId} slugId={pageId} customRenderId={topBannerCustomRenderId} schema="cat_sponsored" />
          </ErrorBoundary>
        </Col>
        <Col xs="12">
          {pageTitle && (
            <ErrorBoundary name="heading">
              <Typography variant="h1" weight="display" height="snug" color="primary" uppercase>
                {pageTitle}
              </Typography>
            </ErrorBoundary>
          )}
          <Row className="isBound">
            <Col sm="2" md="2" lg="2">
              <ErrorBoundary name="side-navigation">
                <div className={cx('educational-layout__side-navigation')}>
                  <Content.SideNavigation />
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="sponsored-skyscraper-banner">
                <Sponsored.SponsoredSkyscraperBanner browserId={sponsoredBrowserId} slugId={pageId} customRenderId={skyscraperBannerCustomRenderId} schema="cat_sponsored" />
              </ErrorBoundary>
            </Col>
            <Col xs="12" sm="10" md="10" lg="10">
              <ErrorBoundary name="hero-carousel">
                <Content.HeroCarousel />
              </ErrorBoundary>
              <ErrorBoundary name="promo-visual-navigation-1">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="promo-visual-navigation-1-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_EARLY_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.promoVisualNavigation1 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="promo-visual-navigation-2">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="promo-visual-navigation-2-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.promoVisualNavigation2 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="promo-visual-navigation-3">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="promo-visual-navigation-3-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.promoVisualNavigation3 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="endcap">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="endcap"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.EndCap
                        storeId={storeId}
                        customerType={customerType.toUpperCase()}
                        pageUrl={`/${pageId}`}
                        narrowBottom
                      />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="spotlight">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="spotlight-section-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.spotlightSection slidesLg={2} slidesXl={3} slidesMd={2} slidesSm={1} />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="sponsored-middle-banner">
                <DynamicComponent
                  bypassFeatureSwitch
                  pageType="educationalLayout"
                  placeholder={(
                    <Placeholder
                      width="100%"
                      height="200px"
                    />
                  )}
                >
                  <Hydrator
                    id="sponsored-middle-banner-hydrator"
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    delay={HYDRATION_STANDARD_DELAY}
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Sponsored.SponsoredMiddleBanner browserId={sponsoredBrowserId} slugId={pageId} customRenderId={middleBannerCustomRenderId} schema="cat_sponsored" />
                  </Hydrator>
                </DynamicComponent>
              </ErrorBoundary>
              <ErrorBoundary name="visual-navigation-1">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="visual-navigation-1-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.visualNavigation1 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="visual-navigation-2">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="visual-navigation-2-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.visualNavigation2 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="visual-navigation-3">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="visual-navigation-3-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.visualNavigation3 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="special-buy-banner">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="special-buy-banner-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.SpecialBuyOfTheDay specialBuyType="sbotd" />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="product-shelf">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="product-shelf-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.ProductShelf />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="resource">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="resource-section-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.resourcesSection />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="medio-inline-1">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="medio-inline-1-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.medioInline1 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="piccola-pencil-1">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="piccola-pencil-1-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.piccolaPencil1 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="dynamic-section">
                {Content.dynamicSections?.map((SubSection, key) => (
                  <ErrorBoundary key={key} name={`dynamicSection-${key}`}>
                    <DynamicComponent
                      bypassFeatureSwitch
                      pageType="educationalLayout"
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Hydrator
                        id={`dynamicSection-${key}-hydrator`}
                        scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                        delay={HYDRATION_STANDARD_DELAY}
                        placeholder={(
                          <Placeholder
                            width="100%"
                            height="200px"
                          />
                        )}
                      >
                        <div className={cx('educational-layout__dynamic-section')}>
                          <SubSection />
                        </div>
                      </Hydrator>
                    </DynamicComponent>
                  </ErrorBoundary>
                ))}
              </ErrorBoundary>
              <ErrorBoundary name="medio-inline-2">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="medio-inline-2-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.medioInline2 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="piccola-pencil-2">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="piccola-pencil-2-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.piccolaPencil2 />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="sponsored-bottom-carousel">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="sponsored-bottom-carousel-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Sponsored.SponsoredBottomCarousel browserId={sponsoredBrowserId} slugId={pageId} customRenderId={bottomCarouselCustomRenderId} schema="cat_sponsored" />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="description">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="description-section-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.Description_New title={descriptionTitle} description={descriptionText} />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="related-search">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="related-search-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      <Content.RelatedSearchesAndProducts navParam={pageId} />
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
              <ErrorBoundary name="seo-links">
                <div className={cx('educational-layout__static-section')}>
                  <DynamicComponent
                    bypassFeatureSwitch
                    pageType="educationalLayout"
                    placeholder={(
                      <Placeholder
                        width="100%"
                        height="200px"
                      />
                    )}
                  >
                    <Hydrator
                      id="seo-links-hydrator"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                      delay={HYDRATION_STANDARD_DELAY}
                      placeholder={(
                        <Placeholder
                          width="100%"
                          height="200px"
                        />
                      )}
                    >
                      {canonicalURL && <Content.SeoLinks path={canonicalURL} />}
                    </Hydrator>
                  </DynamicComponent>
                </div>
              </ErrorBoundary>
            </Col>
          </Row>
        </Col>
      </Row>
    </Row>
  );
});

EducationalLayoutRenderer.displayName = 'EducationalLayoutRenderer';

EducationalLayoutRenderer.propTypes = {
  canonicalURL: string,
  data: shape({}).isRequired,
  pageId: string.isRequired,
  storeId: string.isRequired,
  customerType: string.isRequired,
  pageTypeComponents: shape({}).isRequired,
  thdSEOBotDetection: bool
};

EducationalLayoutRenderer.defaultProps = {
  canonicalURL: null,
  thdSEOBotDetection: false
};
